import type {
  RenderRequest,
  RenderResponse,
  Result,
} from '@tsagko/label-contracts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getPrinterProfile } from '@tsagko/label-printer-profiles'
import { useAdapter } from '../adapters/AdapterProvider'

const DEBOUNCE_MS = 2_000

export interface UseRenderArgs {
  zpl: string | null
  printerProfileId: string
  widthMm: number
  heightMm: number
  /**
   * True when the caller's `resolvePrintRotation(template, profile)` (the
   * same pipeline function `generateZpl` itself calls — see PreviewPane)
   * determined this label prints rotated 90°/270°. When true, the request's
   * `dims` are sent PHYSICAL (width/height swapped) so the render request,
   * Neodynamic's own canvas, and the response's pixel arithmetic all agree
   * with the physical orientation the ZPL body was actually generated for
   * (generate-zpl.ts swaps `^PW`/`^LL` the same way via acrossHeadMm/feedMm).
   * Sending the un-rotated design dims here while the ZPL body is rotated
   * is issue #133's request/render mismatch. Defaults to false (unchanged
   * pre-#133 behavior).
   */
  rotated?: boolean
  /**
   * When true, the debounced auto-render on `request` change is suppressed.
   * Manual render (renderNow) still fires unconditionally. Use this to avoid
   * sending a render request for empty ZPL (^XA…^XZ only) that Neodynamic
   * would 500 on, while still allowing the operator to click "Render" to
   * inspect the raw ZPL in the viewer.
   */
  skipAutoRender?: boolean
}

export interface UseRenderState {
  status: 'idle' | 'pending' | 'ok' | 'error'
  blobUrl: string | null
  error: { code: string; message: string } | null
}

export interface UseRenderApi extends UseRenderState {
  renderNow: () => void
}

export function useRender({
  zpl,
  printerProfileId,
  widthMm,
  heightMm,
  rotated = false,
  skipAutoRender = false,
}: UseRenderArgs): UseRenderApi {
  const adapter = useAdapter()
  const [state, setState] = useState<UseRenderState>({
    status: 'idle',
    blobUrl: null,
    error: null,
  })
  const ctlRef = useRef<AbortController | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  const request: RenderRequest | null = useMemo(() => {
    if (!zpl) return null
    const profile = getPrinterProfile(printerProfileId)
    if (!profile) return null
    return {
      zpl,
      dims: {
        widthMm: rotated ? heightMm : widthMm,
        heightMm: rotated ? widthMm : heightMm,
        dpi: profile.dpi,
      },
    }
  }, [zpl, printerProfileId, widthMm, heightMm, rotated])

  const fire = useCallback(async () => {
    if (!request) return
    if (!adapter) return
    ctlRef.current?.abort()
    const ctl = new AbortController()
    ctlRef.current = ctl
    setState(s => ({ ...s, status: 'pending', error: null }))
    const result: Result<RenderResponse> = await adapter.operations.render(
      request,
      ctl.signal
    )
    if (ctl.signal.aborted) return
    if (result.status === 'ok') {
      // The contract types `png` as `Uint8Array` (`ArrayBufferLike`-backed),
      // but the `lib.dom.d.ts` shipped with TS 5.7 narrowed `BlobPart` to
      // `Uint8Array<ArrayBuffer>` — rejecting `SharedArrayBuffer`-backed
      // views. The bytes coming back from `atob` are always plain
      // `ArrayBuffer`-backed; cast to `BlobPart` to satisfy the lib types
      // without copying.
      const blob = new Blob([result.data.png as unknown as BlobPart], {
        type: 'image/png',
      })
      const url = URL.createObjectURL(blob)
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = url
      setState({ status: 'ok', blobUrl: url, error: null })
    } else {
      setState({
        status: 'error',
        blobUrl: blobUrlRef.current,
        error: { code: result.error.code, message: result.error.message },
      })
    }
  }, [adapter, request])

  // Debounced auto-render on `request` change.
  useEffect(() => {
    if (!request) {
      setState({ status: 'idle', blobUrl: null, error: null })
      return
    }
    // When skipAutoRender is true, the auto-render is suppressed: the request
    // is valid but we don't fire it automatically (e.g. empty template ZPL
    // that the backend would 500 on). renderNow still fires unconditionally.
    if (skipAutoRender) return
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      void fire()
    }, DEBOUNCE_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [fire, request, skipAutoRender])

  // Cleanup on unmount.
  useEffect(
    () => () => {
      ctlRef.current?.abort()
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
    },
    []
  )

  const renderNow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    void fire()
  }, [fire])

  return { ...state, renderNow }
}
